import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setSplitRatio } from "@/state/slices/tabsSlice";
import styles from "./SplitDivider.module.css";

/** Vertical drag handle between the two split panes. Position pulled
 *  from `tabs.splitRatio` (0..1, fraction of the LEFT pane). Drag
 *  updates Redux on every mousemove — the chart slots reposition via
 *  inline style and their ResizeObserver picks up the new width to
 *  re-layout the lightweight-charts canvases.
 *
 *  Walks UP from itself to find the nearest `position: relative`
 *  parent (the chartHost) and uses its bounding rect to convert
 *  cursor X to a ratio. Avoids prop-drilling a host ref while still
 *  being framework-agnostic to layout changes. */
export function SplitDivider() {
  const dispatch = useAppDispatch();
  const splitRatio = useAppSelector((s) => s.tabs.splitRatio);
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  // Document-level mousemove/up while dragging — the wider hit area
  // on the divider itself is enough to start a drag, but the cursor
  // will leave that 6px band almost immediately during a fast drag.
  useEffect(() => {
    if (!dragging) return;
    const host = ref.current?.parentElement; // chartHost
    if (!host) return;
    const onMove = (e: MouseEvent) => {
      const rect = host.getBoundingClientRect();
      if (rect.width <= 0) return;
      const ratio = (e.clientX - rect.left) / rect.width;
      dispatch(setSplitRatio(ratio));
    };
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    // Prevent text selection on the page while the drag is active.
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [dragging, dispatch]);

  return (
    <div
      ref={ref}
      className={`${styles.divider} ${dragging ? styles.dividerDragging : ""}`}
      style={{ left: `${splitRatio * 100}%` }}
      onMouseDown={(e) => {
        if (e.button !== 0) return; // left button only
        e.preventDefault();
        setDragging(true);
      }}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize chart split"
    >
      <div className={styles.line} />
    </div>
  );
}
